import request from '../utils/promisifiedRequest'
import Promise from 'bluebird'
// import R from 'ramda'
import Immutable from 'immutable'
import Qs from 'qs'

/**
 * This implements a single-store Relay-like idea
 * in an isomorphic fashion tailored to the API
 */
class Store {
  constructor (app, creds) {
    this.data = {}
    this.storage = global.localStorage ? global.localStorage : {}
    this.authenticated = !!creds
    this.assumingUser = creds && creds.assume
    this._userCreds = creds

    this._extUrl = app
      ? app.get('baseUrl')
      : `${global.location.protocol}//${global.location.host}`

    // If we are on the server, include base
    this._baseUri = app
      ? `http://localhost:${app.get('port')}`
      : this._extUrl
  }

  /**
   * Populate store with existing state
   * @param  {Object} state
   */
  hydrate ({ metadata, data, authenticated, _userCreds, assumingUser }) {
    for (const key in data) this.data[key] = Immutable.fromJS(data[key])
    this.authenticated = authenticated
    this.assumingUser = assumingUser
    this.metadata = Immutable.fromJS(metadata)
    this._userCreds = _userCreds
  }

  /**
   * Get store state in a hydrateable form
   * @return {Object} state
   */
  dehydrate () {
    return {
      metadata: this.metadata.toJS(),
      data: this.data,
      authenticated: this.authenticated,
      assumingUser: this.assumingUser,
      _userCreds: this._userCreds
    }
  }

  /**
   * Log in with the user's credentials
   * @param {String} creds.username the username
   * @param {String} creds.password the password
   * @return {Promise}              is valid user
   */
  async login (creds) {
    // Validate credentials against API, will throw if invalid
    await request.head('/api/v1/auth')
      .auth(creds.username, creds.password)
      .execAsync()

    this.authenticated = true
    this._userCreds = creds

    // Store authorization on server
    await request.head('/login')
      .auth(creds.username, creds.password)
      .execAsync()
  }

  /**
   * Log out
   */
  logout () {
    // Reset state
    this.authenticated = false
    this._userCreds = null
    this.data = {}
    return request.head('/logout').execAsync()
  }

  /**
   * Create a basic auth url for an endpoing
   * @param  {String} path api resource
   * @return {String}      url of api endpoint with authentication
   */
  getUrl (path) {
    const { username, password } = this._userCreds

    const url = /^http/.test(path)
      ? path
      : `${this._extUrl}/api/v1${path}`

    return url
      .replace(/(https?:\/\/)/, `$1${encodeURIComponent(username)}:${encodeURIComponent(password)}@`)
  }

  /**
   * GET API data
   * @param  {String} path   api resource
   * @param  {Object} params key/value pair of query parameters
   * @return {Promise}       the json response data
   */
  async get (path, params) {
    const isAbsolute = /~/.test(path)
    const uri = isAbsolute
      ? `${this._baseUri}${path.replace('~','')}`
      : `${this._baseUri}/api/v1${path}`

    const res = await request.get(uri)
      .query(Qs.stringify(params))
      .auth(this._userCreds.username, this._userCreds.password)
      .execAsync()

    return Immutable.fromJS(res.body || res.text)
  }

  /**
   * Download
   * @param  {String} path   api resource
   */
  download (path) {
    global.document.location = `${this._extUrl}/api/v1${path}`
  }

  /**
   * POST API data
   * @param  {String} path   api resource
   * @param  {Object} params key/value pair of post data
   * @return {Promise}       the json response data
   */
  async post (path, params) {
    const isAbsolute = /~/.test(path)
    const uri = isAbsolute
      ? `${this._baseUri}${path.replace('~','')}`
      : `${this._baseUri}/api/v1${path}`

    const res = await request.post(uri)
      .auth(this._userCreds.username, this._userCreds.password)
      .send(params)
      .execAsync()

    return Immutable.fromJS(res.body)
  }

  /**
   * PATCH API data
   * @param  {String} path   api resource
   * @param  {Object} params key/value pair of patch data
   * @return {Promise}       the json response data
   */
  async patch (path, params) {
    const isAbsolute = /~/.test(path)
    const uri = isAbsolute
      ? `${this._baseUri}${path.replace('~','')}`
      : `${this._baseUri}/api/v1${path}`

    const res = await request.patch(uri)
      .auth(this._userCreds.username, this._userCreds.password)
      .send(params)
      .execAsync()

    return Immutable.fromJS(res.body)
  }

  /**
   * PUT API data
   * @param  {String} path   api resource
   * @param  {Object} data   data to send
   * @return {Promise}       the json response data
   */
  async put (path, data) {
    const isAbsolute = /~/.test(path)
    const uri = isAbsolute
      ? `${this._baseUri}${path.replace('~','')}`
      : `${this._baseUri}/api/v1${path}`

    const res = await request.put(uri)
      .auth(this._userCreds.username, this._userCreds.password)
      .send(data)
      .execAsync()

    return Immutable.fromJS(res.body)
  }

  /**
   * DELETE API data
   * @param  {String} path   api resource
   * @return {Promise}       the json response data
   */
  async delete (path) {
    const isAbsolute = /~/.test(path)
    const uri = isAbsolute
      ? `${this._baseUri}${path.replace('~','')}`
      : `${this._baseUri}/api/v1${path}`

    const res = await request.del(uri)
      .auth(this._userCreds.username, this._userCreds.password)
      .execAsync()

    return Immutable.fromJS(res.body)
  }

  /**
   * Deactivates group account
   */
  async deactivate (account) {
    await request
      .get(`/account/${account.get('id')}/deactivate/${account.getIn(['group','id'])}`)
      .execAsync()

    global.document.location = '/account/exit-survey'
  }

  /**
   * A Relay-like way of handling data access
   * To perhaps be replaced with Relay in the future
   * Methods requiring data will need a static fetchData method
   * @param  {Object} state  current route state
   * @return {Promise}       finished fetching
   */
  async fetchData (state) {
    /**
     * Aggregate endpoints into one set of requests
     * Child components will override parent components
     */
    const aggregate = state.routes
      .reduce((acc, route) => {
        const { handler: { fetchData }} = route
        // No date to fetch for route
        if (!fetchData) return acc
        const endpoints = fetchData(this, state)
        for (const key in endpoints) {
          acc[key] = endpoints[key]
        }
        return acc
      }, {})

    this.data = await Promise.props(aggregate)

    // Debug logging
    // if (process.env.NODE_ENV !== 'production') {
    //   const current = R.last(state.routes)
    //   console.log(current.name, JSON.parse(JSON.stringify(this.data)))
    // }
    return this.data
  }
}

export default Store



// WEBPACK FOOTER //
// ./modules/shared/store.js